/**
 * AdminMarketplacePricing — Moteur de pricing admin (Iter385)
 *
 * Pilote la monétisation des features premium (verify_signature, qualiopi_export, etc.)
 * avec :
 *  • CRUD complet sur les règles
 *  • Activation/désactivation MANUELLE (jamais automatique)
 *  • Historique + rollback
 *  • Scopes (global / user / company / promo_code) + priorités
 *
 * Aucun hardcoding tarifaire — tout vit en base via le pricing_engine.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Loader2, Power, PowerOff, History, Trash2, X, Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '../../config/environment';
import { useAuth } from '../../contexts/AuthContext';

const API = getApiUrl();
const EMPTY_FORM = {
  feature_key: '', label: '', kind: 'standard', amount_cents: 0,
  currency: 'EUR', scope: 'global', scope_value: '', priority: 50,
  starts_at: '', ends_at: '',
};

export default function AdminMarketplacePricing() {
  const { token } = useAuth();
  const [data, setData] = useState({ items: [], allowed_kinds: [], allowed_scopes: [] });
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // { mode: 'create' | 'edit', form, id? }
  const [saving, setSaving] = useState(false);
  const [historyOf, setHistoryOf] = useState(null); // { ruleId, history }
  const [filterFeature, setFilterFeature] = useState('');
  const [filterActive, setFilterActive] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filterFeature) qs.set('feature_key', filterFeature);
      if (filterActive) qs.set('is_active', filterActive === 'active' ? 'true' : 'false');
      const res = await fetch(`${API}/admin/marketplace/pricing/rules?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Accès refusé');
      const json = await res.json();
      setData(json);
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [token, filterFeature, filterActive]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const openCreate = () => setEditor({ mode: 'create', form: { ...EMPTY_FORM } });
  const openEdit = (rule) => setEditor({
    mode: 'edit', id: rule.id,
    form: {
      feature_key: rule.feature_key || '', label: rule.label || '',
      kind: rule.kind, amount_cents: rule.amount_cents || 0, currency: rule.currency || 'EUR',
      scope: rule.scope, scope_value: rule.scope_value || '', priority: rule.priority || 50,
      starts_at: rule.starts_at || '', ends_at: rule.ends_at || '',
    },
  });

  const save = async () => {
    setSaving(true);
    try {
      const f = editor.form;
      const payload = {
        feature_key: f.feature_key, label: f.label || null,
        kind: f.kind, amount_cents: Number(f.amount_cents || 0), currency: f.currency,
        scope: f.scope, scope_value: f.scope_value || null,
        priority: Number(f.priority || 50),
        starts_at: f.starts_at || null, ends_at: f.ends_at || null,
      };
      const url = editor.mode === 'create'
        ? `${API}/admin/marketplace/pricing/rules`
        : `${API}/admin/marketplace/pricing/rules/${editor.id}`;
      const method = editor.mode === 'create' ? 'POST' : 'PATCH';
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Erreur');
      toast.success(editor.mode === 'create' ? 'Règle créée (inactive — active manuellement)' : 'Règle modifiée');
      setEditor(null); load();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const doAction = async (rule, action) => {
    let body = '{}';
    if (action === 'rollback') {
      const idx = parseInt(window.prompt('Index history à restaurer (0 = plus ancien) :'), 10);
      if (isNaN(idx)) return;
      body = JSON.stringify({ history_index: idx });
    }
    const labels = { activate: 'Activer', deactivate: 'Désactiver', delete: 'Supprimer', rollback: 'Rollback' };
    if (!window.confirm(`${labels[action]} la règle "${rule.label || rule.feature_key}" ?`)) return;
    try {
      const method = action === 'delete' ? 'DELETE' : 'POST';
      const res = await fetch(`${API}/admin/marketplace/pricing/rules/${rule.id}/${action === 'delete' ? '' : action}`.replace(/\/$/, ''), {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: action !== 'delete' ? body : undefined,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Erreur');
      toast.success(`Action ${labels[action]} OK`);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const showHistory = async (rule) => {
    try {
      const res = await fetch(`${API}/admin/marketplace/pricing/rules/${rule.id}/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Erreur');
      setHistoryOf({ ruleId: rule.id, history: json.history, label: rule.label || rule.feature_key });
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F3]" data-testid="admin-marketplace-pricing-page">
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-[#E8E2D2]">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/admin/modules" data-testid="admin-pricing-back-btn" className="inline-flex items-center gap-1.5 text-[#6B5E48] text-sm hover:text-[#1F2937]">
            <ArrowLeft className="w-4 h-4" /> Retour Dashboard
          </Link>
          <span className="text-[#A89F8A] text-xs">·</span>
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#92710A] font-semibold">Administration · Pricing Marketplace</span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-light text-[#1F1A12] mb-1" style={{ fontFamily: 'Georgia, serif' }}>Pricing & Promotions</h1>
            <p className="text-sm text-[#6B5E48]">Moteur de tarification 100% piloté admin. <b>Aucune activation automatique.</b></p>
          </div>
          <button data-testid="pricing-create-btn" onClick={openCreate} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#1F1A12] shadow-[0_2px_8px_rgba(180,150,40,0.25)]">
            <Plus className="w-4 h-4" /> Nouvelle règle
          </button>
        </div>

        {/* Filtres */}
        <div className="flex items-center gap-3 flex-wrap">
          <input data-testid="pricing-filter-feature" type="text" placeholder="Filtre feature_key…" value={filterFeature} onChange={(e) => setFilterFeature(e.target.value)} className="px-3 py-2 rounded-lg border border-[#E8E2D2] bg-white text-sm" />
          <select data-testid="pricing-filter-active" value={filterActive} onChange={(e) => setFilterActive(e.target.value)} className="px-3 py-2 rounded-lg border border-[#E8E2D2] bg-white text-sm">
            <option value="">Tous les états</option>
            <option value="active">Actives uniquement</option>
            <option value="inactive">Inactives uniquement</option>
          </select>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#92710A] animate-spin" /></div>
        ) : data.items.length === 0 ? (
          <div className="bg-white border border-[#E8E2D2] rounded-2xl p-12 text-center text-[#6B5E48]">Aucune règle. Crée la première via le bouton ci-dessus.</div>
        ) : (
          <div className="bg-white border border-[#E8E2D2] rounded-2xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#FBF9F3] border-b border-[#E8E2D2]">
                <tr className="text-left text-xs uppercase tracking-wider text-[#6B5E48]">
                  <th className="px-4 py-3 font-semibold">Feature / Label</th>
                  <th className="px-4 py-3 font-semibold">Kind</th>
                  <th className="px-4 py-3 font-semibold">Scope</th>
                  <th className="px-4 py-3 font-semibold text-right">Prix</th>
                  <th className="px-4 py-3 font-semibold text-center">Prio</th>
                  <th className="px-4 py-3 font-semibold text-center">État</th>
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F2EDE2]">
                {data.items.map((r) => (
                  <tr key={r.id} className="hover:bg-[#FBF9F3]" data-testid={`pricing-row-${r.id}`}>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-mono text-xs text-[#92710A]">{r.feature_key}</div>
                      <div className="text-[#1F1A12]">{r.label || '—'}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#6B5E48]">{r.kind}</td>
                    <td className="px-4 py-3 text-xs text-[#6B5E48]">
                      {r.scope}
                      {r.scope_value && <span className="font-mono ml-1">/{r.scope_value}</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-right tabular-nums text-[#1F1A12]">
                      {((r.amount_cents || 0) / 100).toFixed(2)} {r.currency}
                    </td>
                    <td className="px-4 py-3 text-xs text-center tabular-nums">{r.priority}</td>
                    <td className="px-4 py-3 text-xs text-center">
                      {r.deleted_at ? <span className="px-2 py-0.5 rounded-full bg-zinc-200 text-zinc-600">supprimée</span>
                        : r.is_active ? <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">active</span>
                        : <span className="px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500">inactive</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        {!r.is_active && !r.deleted_at && (
                          <button data-testid={`pricing-activate-${r.id}`} onClick={() => doAction(r, 'activate')} title="Activer" className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700"><Power className="w-3.5 h-3.5" /></button>
                        )}
                        {r.is_active && !r.deleted_at && (
                          <button data-testid={`pricing-deactivate-${r.id}`} onClick={() => doAction(r, 'deactivate')} title="Désactiver" className="p-1.5 rounded hover:bg-amber-50 text-amber-700"><PowerOff className="w-3.5 h-3.5" /></button>
                        )}
                        <button data-testid={`pricing-edit-${r.id}`} onClick={() => openEdit(r)} title="Éditer" className="p-1.5 rounded hover:bg-zinc-100 text-[#6B5E48]"><Save className="w-3.5 h-3.5" /></button>
                        <button data-testid={`pricing-history-${r.id}`} onClick={() => showHistory(r)} title="Historique" className="p-1.5 rounded hover:bg-zinc-100 text-[#6B5E48]"><History className="w-3.5 h-3.5" /></button>
                        {!r.deleted_at && (
                          <button data-testid={`pricing-delete-${r.id}`} onClick={() => doAction(r, 'delete')} title="Supprimer" className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Modal editor */}
      {editor && (
        <div className="fixed inset-0 z-30 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditor(null)}>
          <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-[#E8E2D2] px-5 py-3 flex items-center justify-between">
              <h2 className="font-semibold text-[#1F1A12]">{editor.mode === 'create' ? 'Nouvelle règle' : 'Éditer la règle'}</h2>
              <button onClick={() => setEditor(null)} className="text-[#6B5E48]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <Field label="Feature key">
                <input data-testid="form-feature-key" type="text" required maxLength={80} placeholder="ex : verify_signature, qualiopi_export" value={editor.form.feature_key} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, feature_key: e.target.value } })} className="w-full px-3 py-2 rounded-lg border border-[#E8E2D2] bg-[#FBF9F3] text-sm font-mono focus:bg-white focus:border-[#92710A]" />
              </Field>
              <Field label="Libellé">
                <input type="text" maxLength={200} placeholder="ex : Vérification certifiée — Standard" value={editor.form.label} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, label: e.target.value } })} className="w-full px-3 py-2 rounded-lg border border-[#E8E2D2] bg-[#FBF9F3] text-sm focus:bg-white focus:border-[#92710A]" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Type">
                  <select data-testid="form-kind" value={editor.form.kind} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, kind: e.target.value } })} className="w-full px-3 py-2 rounded-lg border border-[#E8E2D2] bg-[#FBF9F3] text-sm focus:bg-white">
                    {(data.allowed_kinds || ['standard', 'promo', 'free_temporary', 'sponsored', 'enterprise_override']).map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </Field>
                <Field label="Scope">
                  <select data-testid="form-scope" value={editor.form.scope} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, scope: e.target.value } })} className="w-full px-3 py-2 rounded-lg border border-[#E8E2D2] bg-[#FBF9F3] text-sm focus:bg-white">
                    {(data.allowed_scopes || ['global', 'user', 'company', 'promo_code']).map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>
              {editor.form.scope !== 'global' && (
                <Field label="Scope value">
                  <input type="text" placeholder={editor.form.scope === 'promo_code' ? 'ex : QUALIOPI14' : 'ex : user_id ou company_id'} value={editor.form.scope_value || ''} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, scope_value: e.target.value } })} className="w-full px-3 py-2 rounded-lg border border-[#E8E2D2] bg-[#FBF9F3] text-sm focus:bg-white" />
                </Field>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Montant (centimes)">
                  <input data-testid="form-amount" type="number" min={0} max={10000000} value={editor.form.amount_cents} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, amount_cents: e.target.value } })} className="w-full px-3 py-2 rounded-lg border border-[#E8E2D2] bg-[#FBF9F3] text-sm tabular-nums focus:bg-white" />
                </Field>
                <Field label="Priorité (0-100)">
                  <input data-testid="form-priority" type="number" min={0} max={100} value={editor.form.priority} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, priority: e.target.value } })} className="w-full px-3 py-2 rounded-lg border border-[#E8E2D2] bg-[#FBF9F3] text-sm tabular-nums focus:bg-white" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Début (ISO ou vide)">
                  <input type="text" placeholder="2026-05-01T00:00:00Z" value={editor.form.starts_at || ''} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, starts_at: e.target.value } })} className="w-full px-3 py-2 rounded-lg border border-[#E8E2D2] bg-[#FBF9F3] text-sm font-mono focus:bg-white" />
                </Field>
                <Field label="Fin (ISO ou vide)">
                  <input type="text" placeholder="2026-05-15T00:00:00Z" value={editor.form.ends_at || ''} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, ends_at: e.target.value } })} className="w-full px-3 py-2 rounded-lg border border-[#E8E2D2] bg-[#FBF9F3] text-sm font-mono focus:bg-white" />
                </Field>
              </div>
              <div className="bg-[#FFFCEC] border border-[#E8DDB5] rounded-lg px-3 py-2 text-xs text-[#6B5E48]">
                ⚠️ La règle est créée <b>inactive</b>. Tu devras l'activer manuellement après création.
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setEditor(null)} className="px-4 py-2 text-sm text-[#6B5E48]">Annuler</button>
                <button data-testid="form-save-btn" disabled={saving || !editor.form.feature_key} onClick={save} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#1F1A12] disabled:opacity-50">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal history */}
      {historyOf && (
        <div className="fixed inset-0 z-30 bg-black/40 flex items-center justify-center p-4" onClick={() => setHistoryOf(null)}>
          <div className="bg-white rounded-2xl max-w-xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-[#E8E2D2] px-5 py-3 flex items-center justify-between">
              <h2 className="font-semibold text-[#1F1A12]">Historique — {historyOf.label}</h2>
              <button onClick={() => setHistoryOf(null)}><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-2 text-xs font-mono">
              {historyOf.history.map((h, i) => (
                <div key={i} className="border border-[#E8E2D2] rounded-lg p-3 bg-[#FBF9F3]">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-semibold text-[#1F1A12]">[{i}] {h.action}</span>
                    <span className="text-[#A89F8A]">{new Date(h.at).toLocaleString('fr-FR')}</span>
                  </div>
                  <div className="text-[10px] text-[#6B5E48]">par {h.by}</div>
                  {h.snapshot && (
                    <button onClick={() => doAction({ id: historyOf.ruleId, label: historyOf.label }, 'rollback')} className="mt-2 inline-flex items-center gap-1 text-[10px] text-[#92710A] hover:underline">
                      <RotateCcw className="w-3 h-3" /> Rollback à cet état
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[#1F1A12] mb-1 block">{label}</span>
      {children}
    </label>
  );
}
